import { useState } from "react"
import { useDispatch, useSelector } from "react-redux"

import "./index.scss"

import { ConfigContainer, Popup } from "components"
import ToggleButton from "react-bootstrap/ToggleButton"
import Button from "react-bootstrap/Button"
import ToggleButtonGroup from "react-bootstrap/ToggleButtonGroup"
import { Title, Container, Text, Select } from "components/Form"
import { FiCopy } from "react-icons/fi"

import { teamsOnLocation, token, message } from "state_management"
import useT from "utils/hooks/useT"

// Team config used by admins.
export default function TeamConfig({ locationId, teamId, onClose }) {
  const { t } = useT("messages")
  const { t: tc } = useT("common")
  const dispatch = useDispatch()
  const team = useSelector((state) => state.teamsOnLocation[locationId].teams.find((team) => team.id === teamId))
  const [name, setName] = useState(team ? team.name : "")
  const [showProjects, setShowProjects] = useState(team ? team.showProjects : true)
  const [showAnalyses, setShowAnalyses] = useState(team ? team.showAnalyses : true)
  const [showLibrarian, setShowLibrarian] = useState(team ? team.showLibrarian : true)
  // Default to false when the team has no showLims yet (backend doesn't return it until
  // SP-E) so the toggle shows a defined "No" state rather than nothing selected.
  const [showLims, setShowLims] = useState(team?.showLims ?? false)
  const [showToken, setShowToken] = useState(false)
  const isCustomerAdmin = useSelector((state) => state.auth.role) === "customer_admin"

  const tokenString = useSelector((state) => state.token.token)

  const [repositoryIds, setRepositoryIds] = useState(team?.repositoryIds || [])
  const [chatbotIds, setChatbotIds] = useState(team?.chatbotIds || [])

  const assignableRepositories = useSelector((state) => state.repositories.assignableRepositories)
  const chatbots = useSelector((state) => state.librarian.chatbots)

  const handleCopy = () => {
    navigator.clipboard
      .writeText(tokenString)
      .then(() => {
        dispatch(message.success(t("adhoc.copiedToClipboard")))
      })
      .catch(() => {
        dispatch(message.error(t("adhoc.couldNotCopyToClipboard")))
      })
  }

  return (
    <ConfigContainer
      title={team ? tc("editTeam") : tc("addTeam")}
      deleteIsImportant
      deleteHandler={team ? () => dispatch(teamsOnLocation.remove(locationId, team.id)).then(onClose) : undefined}
      submitHandler={() => {
        // SP-E Phase 1.1: the backend now persists `show_lims`, so it is sent on team
        // create/edit (the Team schema accepts it). LIMS is opt-in (default false).
        const res = { name, showProjects, showAnalyses, showLibrarian, showLims, repositoryIds, chatbotIds }
        if (team) {
          dispatch(teamsOnLocation.edit(locationId, team.id, res)).then(onClose)
        } else {
          dispatch(teamsOnLocation.create(locationId, res)).then(onClose)
        }
      }}
      cancelHandler={onClose}
    >
      {showToken && (
        <Popup
          nonScrollable
          className="tokenPopupContainer"
          onCancel={() => {
            dispatch(token.hideToken()) // Delete the token from redux
            setShowToken(false)
          }}
        >
          <div>
            <h4>{tc("permanentApiToken")}</h4>
            <p>
              {tc("permanentApiTokenDescription")}
            </p>
            <Container>
              <Title>{tc("apiToken")}</Title>
              <Title>
                {tokenString}{" "}
                <span title={tc("copyToClipboard")} className="teamconfig-copy-clipboard">
                  <FiCopy onClick={handleCopy} />
                </span>
              </Title>
            </Container>
          </div>
          <Button
            className="close-popup-button"
            variant="primary"
            onClick={() => {
              dispatch(token.hideToken()) // Delete the token from redux
              setShowToken(false)
            }}
          >
            {tc("ok")}
          </Button>
        </Popup>
      )}
      <div className="container-compartment">
        <Container>
          <Title>{tc("teamName")}</Title>
          <Text value={name} required onChange={setName} />
        </Container>
        <Container>
          <Title>{tc("showProjects")}</Title>
          <ToggleButtonGroup
            type="radio"
            className="TeamConfig-toggleButtonGroup"
            name="showProjects"
            defaultValue={showProjects}
            onChange={setShowProjects}
          >
            <ToggleButton
              value={true}
              variant="light"
              id="teamconfig-showprojects_yes"
              className="TeamConfig-radioButton"
            >
              {tc("yes")}
            </ToggleButton>
            <ToggleButton
              value={false}
              variant="light"
              id="teamconfig-showprojects_no"
              className="TeamConfig-radioButton"
            >
              {tc("no")}
            </ToggleButton>
          </ToggleButtonGroup>
        </Container>
        <Container>
          <Title>{tc("showAnalyses")}</Title>
          <ToggleButtonGroup
            type="radio"
            className="TeamConfig-toggleButtonGroup"
            name="showAnalyses"
            defaultValue={showAnalyses}
            onChange={setShowAnalyses}
          >
            <ToggleButton
              value={true}
              variant="light"
              id="teamconfig-showanalyses_yes"
              className="TeamConfig-radioButton"
            >
              {tc("yes")}
            </ToggleButton>
            <ToggleButton
              value={false}
              variant="light"
              id="teamconfig-showanalyses_no"
              className="TeamConfig-radioButton"
            >
              {tc("no")}
            </ToggleButton>
          </ToggleButtonGroup>
        </Container>
        {isCustomerAdmin && (
        <Container>
          <Title>{tc("showLibrarian")}</Title>
          <ToggleButtonGroup
            type="radio"
            className="TeamConfig-toggleButtonGroup"
            name="showLibrarian"
            defaultValue={showLibrarian}
            onChange={setShowLibrarian}
          >
            <ToggleButton
              value={true}
              variant="light"
              id="teamconfig-showlibrarian_yes"
              className="TeamConfig-radioButton"
            >
              {tc("yes")}
            </ToggleButton>
            <ToggleButton
              value={false}
              variant="light"
              id="teamconfig-showlibrarian_no"
              className="TeamConfig-radioButton"
            >
              {tc("no")}
            </ToggleButton>
          </ToggleButtonGroup>
        </Container>
        )}
        {isCustomerAdmin && (
        <Container>
          <Title>{tc("showLims")}</Title>
          <ToggleButtonGroup
            type="radio"
            className="TeamConfig-toggleButtonGroup"
            name="showLims"
            defaultValue={showLims}
            onChange={setShowLims}
          >
            <ToggleButton
              value={true}
              variant="light"
              id="teamconfig-showlims_yes"
              className="TeamConfig-radioButton"
            >
              {tc("yes")}
            </ToggleButton>
            <ToggleButton
              value={false}
              variant="light"
              id="teamconfig-showlims_no"
              className="TeamConfig-radioButton"
            >
              {tc("no")}
            </ToggleButton>
          </ToggleButtonGroup>
        </Container>
        )}
      </div>
      <div className="container-compartment">
        <p>{tc("repositories")}</p>
        <Container>
          <Select
            name={tc("repositories")}
            collection={assignableRepositories}
            value={repositoryIds}
            onChange={setRepositoryIds}
            menuPosition="fixed"
          />
        </Container>
      </div>
      <div className="container-compartment">
        <p>{tc("chatbots")}</p>
        <Container>
          <Select
            name={tc("chatbots")}
            collection={chatbots}
            value={chatbotIds}
            onChange={setChatbotIds}
            menuPosition="fixed"
          />
        </Container>
      </div>
    </ConfigContainer>
  )
}
